import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getMaintenanceHistory,
  getRemainingTo50h,
  getRemainingTo100h,
  deleteMaintenance,
  performMaintenance50,
  performMaintenance100,
  setMaintenanceCost,
  setMaintenanceInfo,
  updateMaintenance,
} from '../../api';
import {
  MaintenanceRecord,
  PerformMaintenanceResult,
  RemainingTO,
} from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { useAircraft } from '../../aircraft/AircraftContext';
import { useFeedback } from '../../hooks/useFeedback';
import { useLoadMore } from '../../hooks/useLoadMore';
import { DateFormat } from '../../utils/DateFormat';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  calendarOverdue,
  formatCalendarWaitingHint,
  formatRemainingHoursDays,
} from '../../utils/toRemaining';
import {
  getMaintenanceNumbers,
  getUpcomingMaintenanceNumbers,
  editBoundsForRecord,
  isTo50Record,
  recordTitle,
} from '../../utils/maintenanceRecord';
import PageBackLink from '../../components/PageBackLink/PageBackLink';
import LoadMoreButton from '../../components/LoadMoreButton/LoadMoreButton';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import MaintenanceDetailsModal from '../../components/MaintenanceDetailsModal/MaintenanceDetailsModal';
import PerformMaintenanceModal, {
  type PerformMaintenancePayload,
} from '../../components/PerformMaintenanceModal/PerformMaintenanceModal';
import PerformResultModal from '../../components/PerformResultModal/PerformResultModal';
import './MaintenancePage.css';

type PerformResultState = {
  title: 'ТО-50' | 'ТО-100';
  data: PerformMaintenanceResult;
};

const persistCostAndInfo = async (
  recordId: number,
  data: PerformMaintenancePayload,
  options: { hadCost: boolean; saveEmptyInfo: boolean }
) => {
  const shouldSaveCost = data.cost != null || data.clear_cost || options.hadCost;
  if (shouldSaveCost) {
    await setMaintenanceCost(
      recordId,
      data.clear_cost ? 0 : data.cost ?? 0,
      data.cost_split || 'hours',
      data.cost_split === 'manual' && data.payer_ids && data.payer_shares
        ? { payer_ids: data.payer_ids, payer_shares: data.payer_shares }
        : undefined
    );
  }
  if ((data.info || '').trim() !== '' || options.saveEmptyInfo) {
    await setMaintenanceInfo(recordId, data.info || '');
  }
};

const MaintenancePage = () => {
  const [records, setRecords] = useState<MaintenanceRecord[]>([]);
  const [to50, setTo50] = useState<RemainingTO | null>(null);
  const [to100, setTo100] = useState<RemainingTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [performType, setPerformType] = useState<'ТО-50' | 'ТО-100' | null>(null);
  const [editingRecord, setEditingRecord] = useState<MaintenanceRecord | null>(
    null
  );
  const [selectedRecord, setSelectedRecord] = useState<MaintenanceRecord | null>(
    null
  );
  const [deletingRecord, setDeletingRecord] = useState<MaintenanceRecord | null>(
    null
  );
  const [performResult, setPerformResult] = useState<PerformResultState | null>(
    null
  );
  const { canMaintain } = useAuth();
  const { aircraftId, dataRevision } = useAircraft();
  const { showError, showSuccess, feedbackNode } = useFeedback();

  const loadData = useCallback(async () => {
    try {
      const [historyRes, to50Res, to100Res] = await Promise.all([
        getMaintenanceHistory(),
        getRemainingTo50h(),
        getRemainingTo100h(),
      ]);
      setRecords(historyRes.data);
      setTo50(to50Res.data);
      setTo100(to100Res.data);
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка загрузки ТО'));
    }
  }, [showError]);

  useEffect(() => {
    void loadData();
  }, [aircraftId, dataRevision, loadData]);

  const handlePerformConfirm = async (data: PerformMaintenancePayload) => {
    if (!performType) return;
    if (performType === 'ТО-100' && !data.reason) {
      showError('Укажите причину проведения ТО');
      return;
    }
    setLoading(true);
    try {
      const res =
        performType === 'ТО-50'
          ? await performMaintenance50({
              after_flight_id: data.after_flight_id,
              done_date: data.done_date,
              mechanic_name: data.mechanic_name,
            })
          : await performMaintenance100({
              after_flight_id: data.after_flight_id,
              done_date: data.done_date,
              reason: data.reason!,
              mechanic_name: data.mechanic_name,
            });
      if (res.data.success) {
        const recordId =
          res.data.id ??
          (await getMaintenanceHistory()).data.find(
            (row) =>
              Math.abs(row.hobbs - res.data.new_last_done_hobbs) < 0.001
          )?.id;
        if (recordId) {
          try {
            await persistCostAndInfo(recordId, data, {
              hadCost: false,
              saveEmptyInfo: false,
            });
          } catch (error) {
            showError(
              getApiErrorMessage(
                error,
                'ТО проведено, но не удалось сохранить стоимость или инфо'
              )
            );
          }
        }
        setPerformResult({ title: performType, data: res.data });
        setPerformType(null);
        await loadData();
      }
    } catch (error) {
      showError(
        getApiErrorMessage(error, `Ошибка при проведении ${performType}`)
      );
    }
    setLoading(false);
  };

  const handleEditConfirm = async (data: PerformMaintenancePayload) => {
    if (!editingRecord) return;
    setLoading(true);
    try {
      await updateMaintenance(editingRecord.id, {
        after_flight_id: data.after_flight_id,
        done_date: data.done_date,
        reason: data.reason,
        mechanic_name: data.mechanic_name,
      });
      await persistCostAndInfo(editingRecord.id, data, {
        hadCost: (editingRecord.cost || 0) > 0,
        saveEmptyInfo: true,
      });
      setEditingRecord(null);
      await loadData();
      showSuccess('ТО обновлено');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка при изменении ТО'));
    }
    setLoading(false);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingRecord) return;
    setLoading(true);
    try {
      await deleteMaintenance(deletingRecord.id);
      setDeletingRecord(null);
      await loadData();
      showSuccess('ТО удалено');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка при удалении ТО'));
    }
    setLoading(false);
  };

  const toNumbers = getMaintenanceNumbers(records);
  const upcomingNumbers = getUpcomingMaintenanceNumbers(records);
  const editingBounds = useMemo(
    () => (editingRecord ? editBoundsForRecord(editingRecord, records) : null),
    [editingRecord, records]
  );
  const latestRecordId = useMemo(() => {
    if (records.length === 0) return null;
    return records.reduce((best, row) =>
      row.hobbs > best.hobbs || (row.hobbs === best.hobbs && row.id > best.id)
        ? row
        : best
    ).id;
  }, [records]);
  const canEditRecord = (record: MaintenanceRecord) => Boolean(canMaintain);
  const canDeleteRecord = (record: MaintenanceRecord) =>
    Boolean(
      canMaintain &&
        (record.can_delete || record.is_latest || record.id === latestRecordId)
    );
  const to100CalendarHint = formatCalendarWaitingHint(to100);
  const { visibleCount, hasMore, remaining, nextBatch, loadMore, loadAll } = useLoadMore(
    records.length
  );
  const visibleRecords = records.slice(0, visibleCount);
  const viewingRecord = selectedRecord
    ? records.find((row) => row.id === selectedRecord.id) || selectedRecord
    : null;

  return (
    <div className="history-page">
      {feedbackNode}
      <div className="history-header">
        <PageBackLink to="/" />
        <h2>ТО</h2>
      </div>

      <div className="to-grid">
        <div
          className={`to-card${to50?.active ? ' active to-50-theme' : ' inactive'}`}
        >
          <div className="to-card-header">
            <div className="to-card-title">
              <h3>ТО-50</h3>
              <strong className="to-card-number">№{upcomingNumbers.to50}</strong>
            </div>
            <div className="to-card-badge">
              {to50?.active ? 'Сейчас' : 'Ожидает'}
            </div>
          </div>
          {to50?.active ? (
            <>
              {to50.status === 'ok' ? (
                <>
                  <small className="to-label">Осталось:</small>
                  <p className="to-number green">{to50.remaining ?? '—'} м/ч</p>
                </>
              ) : (
                <>
                  <p className="to-number red">{to50.remaining ?? '—'} м/ч</p>
                  <small>Пора проводить ТО-50</small>
                </>
              )}
              {canMaintain && (
                <button
                  className="to-button to-50"
                  onClick={() => setPerformType('ТО-50')}
                  disabled={loading}
                >
                  Провести ТО-50
                </button>
              )}
            </>
          ) : (
            <>
              <div className="to-waiting-mark" aria-hidden>
                ···
              </div>
              <small className="to-hint">{to50?.hint || 'Сначала проведите ТО-100'}</small>
            </>
          )}
        </div>

        <div
          className={`to-card${to100?.active ? ' active to-100-theme' : ' inactive'}`}
        >
          <div className="to-card-header">
            <div className="to-card-title">
              <h3>ТО-100</h3>
              <strong className="to-card-number">№{upcomingNumbers.to100}</strong>
            </div>
            <div className="to-card-badge">
              {to100?.active ? 'Сейчас' : 'Ожидает'}
            </div>
          </div>
          {to100?.active ? (
            <>
              {to100.status === 'ok' ? (
                <>
                  <small className="to-label">Осталось:</small>
                  <p className="to-number green">{formatRemainingHoursDays(to100)}</p>
                </>
              ) : (
                <>
                  <p className="to-number red">{formatRemainingHoursDays(to100)}</p>
                  <small>
                    {to100.status === 'blocked'
                      ? 'Блок: больше 115 м/ч'
                      : calendarOverdue(to100) &&
                          (to100.remaining == null || Number(to100.remaining) > 0)
                        ? 'Просрочено: прошёл год с ТО-100'
                        : 'Просрочено (допуск до 115 м/ч)'}
                  </small>
                </>
              )}
              {to100.hard_remaining != null && (
                <small>
                  {Number(to100.hard_remaining) < 0
                    ? 'Полёты запрещены'
                    : `Запрет через: ${to100.hard_remaining} м/ч`}
                </small>
              )}
              {canMaintain && (
                <button
                  className="to-button to-100"
                  onClick={() => setPerformType('ТО-100')}
                  disabled={loading}
                >
                  Провести ТО-100
                </button>
              )}
            </>
          ) : (
            <>
              <div className="to-waiting-mark" aria-hidden>
                ···
              </div>
              <small className="to-hint">{to100?.hint || 'Сначала проведите ТО-50'}</small>
              {to100CalendarHint ? (
                <small className="to-hint">{to100CalendarHint}</small>
              ) : null}
            </>
          )}
        </div>
      </div>

      <h3 className="history-subtitle">История</h3>
      {records.length > 0 ? (
        <p className="history-actions-hint">
          Нажмите на карточку, чтобы открыть запись
        </p>
      ) : null}

      {records.length === 0 && (
        <div className="history-empty">Нет записей о ТО</div>
      )}

      {visibleRecords.map((r) => {
        const isTo50 = isTo50Record(r);
        const title = recordTitle(r);
        return (
        <div
          key={r.id}
          className={`history-card is-clickable${isTo50 ? ' to-50' : ' to-100'}`}
          role="button"
          tabIndex={0}
          title="Нажмите, чтобы открыть запись"
          onClick={() => setSelectedRecord(r)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setSelectedRecord(r);
            }
          }}
        >
          <div className="history-card-header">
            <span
              className={`history-type-badge${
                isTo50 ? ' to-50' : ' to-100'
              }`}
            >
              {title}
            </span>
            <strong className="history-card-number">№{toNumbers[r.id]}</strong>
          </div>
          <div className="history-card-meta">
            <div className="history-meta-row">
              <span>Дата</span>
              <strong>{DateFormat(String(r.date))}</strong>
            </div>
            <div className="history-meta-row">
              <span>Наработка</span>
              <strong>{r.hobbs} м/ч</strong>
            </div>
            {r.mechanic ? (
              <div className="history-meta-row">
                <span>Кем проведено</span>
                <strong>{r.mechanic}</strong>
              </div>
            ) : null}
          </div>
        </div>
        );
      })}
      {hasMore ? (
        <LoadMoreButton
          remaining={remaining}
          nextBatch={nextBatch}
          onClick={loadMore}
          onShowAll={loadAll}
        />
      ) : null}

      {viewingRecord && !editingRecord && !deletingRecord ? (
        <MaintenanceDetailsModal
          record={viewingRecord}
          number={toNumbers[viewingRecord.id]}
          canEdit={canEditRecord(viewingRecord)}
          canDelete={canDeleteRecord(viewingRecord)}
          onClose={() => setSelectedRecord(null)}
          onEdit={() => {
            setEditingRecord(viewingRecord);
            setSelectedRecord(null);
          }}
          onDelete={() => {
            setDeletingRecord(viewingRecord);
            setSelectedRecord(null);
          }}
        />
      ) : null}

      {performType && (
        <PerformMaintenanceModal
          title={performType}
          lastDoneHobbs={
            performType === 'ТО-50'
              ? to50?.period_start_hobbs ?? to50?.last_done_hobbs ?? 0
              : to100?.period_start_hobbs ?? to100?.last_done_hobbs ?? 0
          }
          submitting={loading}
          onClose={() => setPerformType(null)}
          onConfirm={handlePerformConfirm}
        />
      )}

      {editingRecord && (
        <PerformMaintenanceModal
          title={isTo50Record(editingRecord) ? 'ТО-50' : 'ТО-100'}
          mode="edit"
          lastDoneHobbs={editingBounds?.minHobbs ?? 0}
          maxHobbs={editingBounds?.maxHobbs}
          initialFlightId={editingRecord.after_flight_id ?? null}
          initialDoneDate={String(editingRecord.date || '')}
          initialReason={editingRecord.reason || ''}
          initialMechanic={editingRecord.mechanic || ''}
          initialRecord={editingRecord}
          submitting={loading}
          onClose={() => setEditingRecord(null)}
          onConfirm={handleEditConfirm}
        />
      )}

      {deletingRecord && (
        <ConfirmModal
          title="Удалить ТО?"
          message="Последняя запись ТО будет удалена. Очередь и наработка пересчитаются по оставшимся записям."
          confirmLabel="Удалить"
          danger
          submitting={loading}
          onCancel={() => setDeletingRecord(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {performResult && (
        <PerformResultModal
          title={performResult.title}
          hobbs={performResult.data.new_last_done_hobbs}
          periodHours={performResult.data.period_hours}
          pilots={performResult.data.pilot_data?.pilots || []}
          onClose={() => setPerformResult(null)}
        />
      )}
    </div>
  );
};

export default MaintenancePage;
